import { FocusTrap } from '@mui/base'
import {
  type FC,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { type CmsComponentMenu } from '@redteclab/api/clients/content-management-system'

import { useHeaderBackdropContext } from '../../Header/headerBackdropContext'

import { HeaderCategoriesList } from './HeaderCategoriesList'
import { HeaderCategoriesMenuIconButton } from './HeaderCategoriesMenuIconButton'

type MenuState = {
  open: boolean

  /**
   * Inspired by MUI Dialog [onClose](https://mui.com/material-ui/api/dialog/#dialog-prop-onClose) prop.
   * Helps to identify further actions after closing the menu.
   */
  reason?: 'escapeKeyDown'
}

type HeaderCategoriesMenuProps = {
  categoriesData: CmsComponentMenu
}

export const HeaderCategoriesMenu: FC<HeaderCategoriesMenuProps> = ({
  categoriesData,
}) => {
  const { hideBackdrop, showBackdrop } = useHeaderBackdropContext()

  const [menuState, setMenuState] = useState<MenuState>({
    open: false,
  })

  const triggerRef = useRef<HTMLButtonElement>(null)

  const onContainerPointerEnter = useCallback(() => {
    setMenuState({ open: true })
    showBackdrop()
  }, [showBackdrop])

  const onContainerPointerLeave = useCallback(() => {
    setMenuState({
      open: false,
    })
    hideBackdrop()
  }, [hideBackdrop])

  const onTriggerClick = useCallback(() => {
    if (!menuState.open) {
      setMenuState({ open: true })
      showBackdrop()
    }
  }, [menuState.open, showBackdrop])

  const onMenuKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuState({
          open: false,
          reason: 'escapeKeyDown',
        })
        hideBackdrop()
      }
    },
    [hideBackdrop],
  )

  useEffect(() => {
    if (!menuState.open && menuState.reason === 'escapeKeyDown') {
      triggerRef.current?.focus()
    }
  }, [menuState.reason, menuState.open])

  return (
    <div
      className="h-full"
      onPointerEnter={onContainerPointerEnter}
      onPointerLeave={onContainerPointerLeave}
      role="menubar"
    >
      <HeaderCategoriesMenuIconButton
        icon={categoriesData.icon}
        menuIsShown={menuState.open}
        onClick={onTriggerClick}
        ref={triggerRef}
      >
        {categoriesData.title}
      </HeaderCategoriesMenuIconButton>

      {menuState.open ? (
        <FocusTrap disableRestoreFocus open>
          <HeaderCategoriesList
            categories={categoriesData.items}
            onKeyDown={onMenuKeyDown}
          />
        </FocusTrap>
      ) : null}
    </div>
  )
}
